/**
 * Edge Detect — Sobel 3×3 gradient magnitude.
 *
 *   Sobel kernels (separable: smoothing × differentiation):
 *     Gx = [ -1  0 +1 ;
 *            -2  0 +2 ;
 *            -1  0 +1 ]    (smooth Y, differentiate X)
 *     Gy = [ -1 -2 -1 ;
 *             0  0  0 ;
 *            +1 +2 +1 ]    (smooth X, differentiate Y)
 *
 *   Magnitude = √(Gx² + Gy²) — applied to Rec.709 luminance.
 *   Output    = grey image with edge strength encoded in all three RGB
 *               channels (so the result is a perceptually-honest edge
 *               map regardless of downstream colour processing).
 *
 * Canny full pipeline (Gaussian pre-smooth + Sobel + non-max suppression
 * + double threshold + hysteresis) is the natural follow-up for Wave
 * 11.4. v1 ships Sobel-only — produces a usable edge map and matches
 * the prompt's deliverable.
 *
 * Optional `threshold` clips weak edges to 0 — gives a cleaner binary-
 * like output when the user wants high contrast.
 *
 * Pipeline slot: destructive pre-kernel commit. Identity short-circuit:
 * we treat 'sobel' with default threshold as "do the work" — there's
 * no natural identity for Edge Detect since the user explicitly asks
 * for it. We DO short-circuit on a sentinel `method: 'none'` so the
 * dispatcher can no-op when the dialog is cancelled.
 *
 * References:
 *   • Sobel, I. (1968) "An Isotropic 3×3 Image Gradient Operator."
 *   • Canny, J. (1986) "A Computational Approach to Edge Detection",
 *     IEEE PAMI 8(6).
 *
 * License: BLRRD is MIT. NO GPLv3 source consulted.
 *
 * @module src/pipeline/filters/spatial/edge-detect
 */

import { mirrorIndex, validateRGBA, LUMA_R, LUMA_G, LUMA_B } from './_util.js';
import { isGPUAvailable, gpuSobel } from './_gpu.js';

/** Slider range for the optional threshold. */
export const EDGE_THRESHOLD_MIN = 0;
export const EDGE_THRESHOLD_MAX = 1;

/**
 * Default parameters. method='none' (identity short-circuit) so the
 * filter is a no-op until the user explicitly chooses 'sobel' in the
 * dialog. This is what lets the pixel-hash + worker-parity baselines
 * stay byte-identical when nothing is applied.
 *
 * @returns {{ method: 'sobel'|'none', threshold: number }}
 */
export function defaultParams() {
    return { method: 'none', threshold: 0 };
}

/**
 * Identity / no-op test. Returns true when the dispatcher should skip
 * the filter (method == 'none').
 *
 * @param {{ method?: string }} params
 * @returns {boolean}
 */
export function isIdentityEdgeDetect(params) {
    if (!params) return true;
    if (params.method === 'none') return true;
    return false;
}

/**
 * Apply Sobel edge detection in-place on a packed RGBA Float32 buffer.
 * Operates on Rec.709 luminance; output is grey (R = G = B = mag).
 *
 * Algorithm:
 *   1. compute luma plane L[i] = 0.2126·R + 0.7152·G + 0.0722·B
 *   2. per pixel: Gx = Sobel-X • L_3x3, Gy = Sobel-Y • L_3x3
 *   3. magnitude = sqrt(Gx² + Gy²)
 *   4. if threshold > 0 and mag < threshold: mag = 0
 *   5. write rgba[r,g,b] = mag (alpha untouched)
 *
 * Boundary: mirror-extend via mirrorIndex.
 *
 * @param {Float32Array} rgba    scene-linear RGBA, length 4·w·h
 * @param {number} w
 * @param {number} h
 * @param {object} params
 * @param {'sobel'|'none'} params.method
 * @param {number} [params.threshold]
 * @returns {Float32Array}       same `rgba` reference (in-place)
 */
export function applyEdgeDetect(rgba, w, h, params) {
    validateRGBA('applyEdgeDetect', rgba, w, h);
    const p = params || {};
    if (isIdentityEdgeDetect(p)) return rgba;

    const threshold = Math.min(
        EDGE_THRESHOLD_MAX,
        Math.max(EDGE_THRESHOLD_MIN, +p.threshold || 0),
    );

    /* Wave 11.4 GPU path — single-pass Sobel. On any GL failure
     * (init, compile, context-lost, readPixels) fall through to the
     * CPU path below. */
    if (isGPUAvailable()) {
        try {
            const out = gpuSobel(rgba, w, h, threshold);
            rgba.set(out);
            return rgba;
        } catch (err) {
            if (typeof globalThis !== 'undefined' && globalThis.BLRRD_DEBUG_GPU) {
                /* eslint-disable-next-line no-console */
                console.warn('[edge-detect] GPU path failed, falling back to CPU:', err);
            }
        }
    }

    const N = w * h;
    /* Build luma plane. */
    const L = new Float32Array(N);
    for (let i = 0, j = 0; i < N; i++, j += 4) {
        L[i] = LUMA_R * rgba[j] + LUMA_G * rgba[j + 1] + LUMA_B * rgba[j + 2];
    }

    /* Convolve in a single pass — Sobel-X and Sobel-Y share the same
     * 3×3 neighbourhood reads, so we fuse them. */
    for (let y = 0; y < h; y++) {
        const yU = mirrorIndex(y - 1, h);
        const yD = mirrorIndex(y + 1, h);
        for (let x = 0; x < w; x++) {
            const xL = mirrorIndex(x - 1, w);
            const xR = mirrorIndex(x + 1, w);

            const tL = L[yU * w + xL];
            const tC = L[yU * w + x ];
            const tR = L[yU * w + xR];
            const mL = L[y  * w + xL];
            /* center cell not used in either kernel */
            const mR = L[y  * w + xR];
            const bL = L[yD * w + xL];
            const bC = L[yD * w + x ];
            const bR = L[yD * w + xR];

            const gx = -tL +  tR
                       -2 * mL + 2 * mR
                       - bL +  bR;
            const gy = -tL - 2 * tC - tR
                       + bL + 2 * bC + bR;

            let mag = Math.sqrt(gx * gx + gy * gy);
            if (mag < threshold) mag = 0;

            const j = (y * w + x) * 4;
            rgba[j]     = mag;
            rgba[j + 1] = mag;
            rgba[j + 2] = mag;
            /* alpha untouched. */
        }
    }
    return rgba;
}
